import React, { useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useSearchParams } from "react-router-dom";

import { useUserOrganizations, useWorkspaces } from "src/api/queries";
import { oauthApi } from "src/api/taktileApi";
import { Button } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { OAuthConsentRequest, OrganizationInDB } from "src/clients/taktile-api";
import { Callout } from "src/design-system/Callout";

type ConsentError = {
  error: string;
  error_description: string;
};

const selectClasses =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-inter-normal-13px text-gray-800 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500";

export const OAuthConsentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("request_id");
  const clientId = searchParams.get("client_id");

  const { data: organizations, isLoading: orgsLoading } =
    useUserOrganizations();
  const { data: workspaces, isLoading: wsLoading } = useWorkspaces();

  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [selectedWsId, setSelectedWsId] = useState<string>("");

  const filteredWorkspaces = workspaces?.filter(
    (ws) => ws.organization_id === selectedOrgId,
  );

  const selectedOrg = organizations?.find((org) => org.id === selectedOrgId);
  const selectedWs = workspaces?.find((ws) => ws.id === selectedWsId);

  const mutation = useMutation({
    mutationFn: (body: OAuthConsentRequest) =>
      oauthApi.consentApiV1OauthConsentPost(body),
    onSuccess: (response) => {
      window.location.href = response.data.redirect_url;
    },
  });

  const errorMessage =
    mutation.error instanceof AxiosError
      ? ((mutation.error.response?.data as ConsentError | undefined)
          ?.error_description ?? "An unexpected error occurred")
      : mutation.isError
        ? "An unexpected error occurred"
        : undefined;

  if (!requestId || !clientId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Callout type="error">
          Invalid authorization request. Missing required parameters.
        </Callout>
      </div>
    );
  }

  const isLoading = orgsLoading || wsLoading;
  const canAuthorize = Boolean(selectedOrgId && selectedWsId);

  const handleOrgChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedOrgId(e.target.value);
    setSelectedWsId("");
  };

  const handleWsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedWsId(e.target.value);
  };

  const handleAuthorize = () => {
    const body: OAuthConsentRequest = {
      request_id: requestId,
      ...(selectedOrg &&
        selectedWs && {
          org_id: selectedOrg.id,
          ws_id: selectedWs.id,
          workspace_slug: selectedWs.slug,
          workspace_name: selectedWs.name,
          workspace_base_url: selectedWs.base_url,
          principal: selectedOrg.principal,
          org_name: selectedOrg.name,
        }),
    };
    mutation.mutate(body);
  };

  const handleDeny = () => {
    mutation.mutate({ request_id: requestId, denied: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-[400px]">
        <Card variant="inverted">
          <div className="space-y-4 p-2">
            <h1 className="font-inter-semibold-15px text-gray-800">
              Authorize {clientId}
            </h1>
            <p className="font-inter-normal-13px text-gray-600">
              Select a workspace to authorize CLI access.
            </p>

            {isLoading ? (
              <p className="font-inter-normal-13px text-gray-500">
                Loading workspaces…
              </p>
            ) : (
              <>
                <div className="space-y-1">
                  <label className="font-inter-normal-13px text-gray-700">
                    Organization
                  </label>
                  <select
                    className={selectClasses}
                    value={selectedOrgId}
                    onChange={handleOrgChange}
                  >
                    <option value="">Select an organization</option>
                    {organizations?.map((org: OrganizationInDB) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="font-inter-normal-13px text-gray-700">
                    Workspace
                  </label>
                  <select
                    className={selectClasses}
                    value={selectedWsId}
                    onChange={handleWsChange}
                    disabled={!selectedOrgId}
                  >
                    <option value="">Select a workspace</option>
                    {filteredWorkspaces?.map((ws: WorkspaceDataplane) => (
                      <option key={ws.id} value={ws.id}>
                        {ws.name}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {errorMessage !== undefined && (
              <Callout type="error">{errorMessage}</Callout>
            )}

            <div className="flex gap-3">
              <Button
                htmlType="button"
                variant="primary"
                loading={mutation.isPending && !mutation.variables?.denied}
                disabled={mutation.isPending || !canAuthorize}
                onClick={handleAuthorize}
                fullWidth
              >
                Authorize
              </Button>
              <Button
                htmlType="button"
                variant="secondary"
                loading={
                  mutation.isPending && mutation.variables?.denied === true
                }
                disabled={mutation.isPending}
                onClick={handleDeny}
                fullWidth
              >
                Deny
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
